import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag } from '@carbon/react';
import { Renew } from '@carbon/react/icons';
import { definePage, defineResource, ListQuery, ReferenceLoad, TenantParams } from '@restavo/crud';
import TransactionForm from 'src/components/Accounting/TransactionForm';
import { TransactionStatusTag, TransactionTypeTag } from 'src/components/Accounting/TransactionTags';
import EmptyState from 'src/components/Base/EmptyState';
import FormattedDate from 'src/components/Base/FormattedDate';
import { useAuthPermissions } from 'src/hooks/useAuthPermissions';
import { useCurrentRestaurantBranch } from 'src/hooks/useRestaurantBranches';
import { useCurrentRestaurant } from 'src/hooks/useRestaurants';
import { useUrlSearchFilter } from 'src/hooks/useUrlSearchFilter';
import {
  orgRestaurantBranches,
  tenantTransactionCategories,
  tenantTransactionCreate,
  tenantTransactionDelete,
  tenantTransactions,
  tenantTransactionUpdate,
} from 'src/services/apis/tenantRequests';
import { BranchScope } from 'src/services/apis/tenantRequests';
import { TransactionEntity } from 'src/services/apis/types';
import { TransactionCategoryTypeEnum, transactionStatusOptions, TransactionTypeEnum } from 'src/utils/accounting';
import { QUERIES } from 'src/utils/react-query';

/**
 * Module-scope loads (spec §7.5): the category column resolves names against the FULL
 * category list — exactly the client-side find the hand-written cell did — while the
 * category FILTER chip offers only the categories matching the route's type, the way the
 * old ComboBox did. Four stable constants (one per route variant) keep the reference
 * cache's identity contract: the config picks between them, never builds one inline.
 */
const categoryReference = (params: TenantParams, query?: { page: number; limit: number }) =>
  tenantTransactionCategories(params, query);

const typeMatchedCategoryReference =
  (type: TransactionCategoryTypeEnum): ReferenceLoad =>
  async (params, query) => {
    const result = await categoryReference(params, query);
    return { ...result, data: result.data.filter((item: any) => item.type === type) };
  };

const incomeCategoryReference = typeMatchedCategoryReference(TransactionCategoryTypeEnum.INCOME);
const expenseCategoryReference = typeMatchedCategoryReference(TransactionCategoryTypeEnum.EXPENSE);
const investmentCategoryReference = typeMatchedCategoryReference(TransactionCategoryTypeEnum.INVESTMENT);

/** The branch column's name source — the same org list useBranches hands the old cell. */
const branchReference = (params: TenantParams) => orgRestaurantBranches(params);

/**
 * The Transactions page config, as a hook because three pieces of it are page state the
 * chip vocabulary cannot express:
 *
 * - the route-derived transaction type, injected as a server-side `type||$eq` filter (the
 *   old hook did the same; it was never a visible control),
 * - an org admin's view is locked to TODAY. Nothing on the server enforces that, so it is not a
 *   chip — a chip can be removed. It rides the list wrapper as `date||$gte`/`date||$lte` and is
 *   shown as a read-only tag. Everyone else filters by date with an ordinary `date` chip in the
 *   filter popover, whose `between` now bounds whole days as the old picker did,
 * - the palette deep-link's `?search=` term, which the old hook seeded its (server-side)
 *   title search from; it bridges into the same `title||$contL` filter until the user
 *   searches through the crud search box (whose term takes over).
 */
export const useTransactionsPage = ({
  expenses,
  incomes,
  investments,
}: {
  expenses?: boolean;
  incomes?: boolean;
  investments?: boolean;
}) => {
  const { t } = useTranslation();
  const { isAdmin } = useAuthPermissions();
  const currentRestaurant = useCurrentRestaurant();
  const currentRestaurantBranch = useCurrentRestaurantBranch();
  const urlSearch = useUrlSearchFilter();

  const transactionType: TransactionTypeEnum | null = expenses
    ? TransactionTypeEnum.EXPENSE
    : incomes
    ? TransactionTypeEnum.INCOME
    : investments
    ? TransactionTypeEnum.INVESTMENT
    : null;

  const transactionTitle = incomes
    ? t('common.income')
    : expenses
    ? t('common.expense')
    : investments
    ? t('common.investment')
    : t('common.transaction');

  // The heading's plural comes from the bundle, not from `${transactionTitle}s` — Vietnamese
  // has no plural -s, so the concatenation used to render "Giao dịchs".
  const transactionTitlePlural = incomes
    ? t('common.incomes')
    : expenses
    ? t('common.expenses')
    : investments
    ? t('common.investments')
    : t('common.transactions');

  const transactions = useMemo(
    () =>
      defineResource({
        key: QUERIES.TRANSACTIONS,
        // The hand-written hook sent limit 20 and drove server page/limit from the
        // pagination bar (page sizes 20/50/100) — the crud layer's pagination is the same
        // machine.
        pageSize: 20,
        api: {
          list: (scope: BranchScope, query?: ListQuery) => {
            const injected: string[] = [];
            if (transactionType != null) {
              injected.push(`type||$eq||${transactionType}`);
            }
            if (isAdmin) {
              // Evaluated per request, so a page left open past midnight moves on to the new day.
              injected.push(`date||$gte||${dayjs().startOf('day')}`);
              injected.push(`date||$lte||${dayjs().endOf('day')}`);
            }
            if (urlSearch && !query?.s) {
              injected.push(`title||$contL||${urlSearch}`);
            }
            return tenantTransactions(scope, { ...query, filter: [...(query?.filter ?? []), ...injected] });
          },
          create: tenantTransactionCreate,
          update: tenantTransactionUpdate,
          remove: tenantTransactionDelete,
        },
        // The hand-written page searched title server-side (debounced $contL filter).
        search: 'server',
        searchFields: ['title'],
        filterable: [
          {
            field: 'categoryId',
            labelKey: 'common.category',
            type: 'reference',
            load:
              transactionType === TransactionTypeEnum.INCOME
                ? incomeCategoryReference
                : transactionType === TransactionTypeEnum.EXPENSE
                ? expenseCategoryReference
                : transactionType === TransactionTypeEnum.INVESTMENT
                ? investmentCategoryReference
                : categoryReference,
            optionLabel: (category: any) => category.name,
          },
          {
            field: 'status',
            labelKey: 'common.status',
            type: 'enum',
            options: transactionStatusOptions.map((option) => ({ id: String(option.id), labelKey: option.text })),
          },
          // Not for an admin: their range is fixed to today (see the hook doc), and a date chip
          // on top of it could only ever narrow to nothing.
          ...(isAdmin ? [] : [{ field: 'date', labelKey: 'common.date', type: 'date' as const }]),
        ],
      }),
    [transactionType, isAdmin, urlSearch]
  );

  // The page state above cannot ride the query key (it is not a crud chip), so the
  // narrowed list is fetched explicitly once the render that captured it has committed.
  const mountedRef = useRef(false);
  const refetchRef = useRef<null | (() => void)>(null);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    refetchRef.current?.();
    // transactionType rides the route: switching tabs reuses this component instance with
    // an unchanged crud query key, so without it here the previous route's rows would stay.
  }, [isAdmin, urlSearch, transactionType]);

  // The form presets the route's type (the old page passed the same prop). forwardRef is
  // load-bearing: CrudPage drives the form through its imperative ref, so a plain wrapper
  // function would silently drop it and every submit would fail loudly at the modal.
  const formType = incomes
    ? TransactionTypeEnum.INCOME
    : expenses
    ? TransactionTypeEnum.EXPENSE
    : investments
    ? TransactionTypeEnum.INVESTMENT
    : TransactionTypeEnum.INCOME;
  const TransactionTypedForm = useMemo(
    () => React.forwardRef((formProps: any, ref: any) => <TransactionForm {...formProps} ref={ref} type={formType} />),
    [formType]
  );

  const page = {
    ...definePage(transactions, {
      titleKey: currentRestaurantBranch?.name ? 'titles.accountingPage' : 'titles.accountingPageWithoutBranch',
      // The old description was `${total} ${title.toLowerCase()}` — the singular keys
      // preserve that lowercase copy byte-for-byte.
      descriptionKey: incomes
        ? 'common.incomeSingular'
        : expenses
        ? 'common.expenseSingular'
        : investments
        ? 'common.investmentSingular'
        : 'common.transactionSingular',
      // "Create New Transaction" — the modal heading's exact pre-migration copy
      // (createTitled had dropped the "New"; the vi key interpolates the same way).
      createLabelKey: 'actions.createNewWithTitle',
      updateLabelKey: 'actions.updateTitled',
      deleteHeadingKey: 'messages.areYouSureToDeleteThis',
      columns: [
        // The column a user identifies a transaction by must survive every prefs
        // reconciliation; the wrapper passes the query through, so the server can sort it.
        { key: 'title', headerKey: 'common.title', sortable: true, alwaysVisible: true },
        {
          key: 'categoryId',
          headerKey: 'common.category',
          display: {
            kind: 'reference',
            load: categoryReference,
            optionLabel: (category: any) => category.name,
          },
        },
        // currency renders via the portal's useMoney formatter — the same one the hand-written
        // cell used, minus the empty-string gap it left whenever total was missing.
        { key: 'total', headerKey: 'common.total', display: { kind: 'currency' } },
        // The old cell showed Today for the current day before any fixed format applied —
        // the declarative date kind has no such case, so the component stays.
        {
          key: 'date',
          headerKey: 'common.date',
          render: (row: TransactionEntity) => <FormattedDate date={row.date} format={'DD/MM/YYYY'} />,
        },
        // The tag components carry the old cells' exact colors and their untranslated
        // English labels ('Income', not a translation key) — ported verbatim.
        {
          key: 'type',
          headerKey: 'common.transactionType',
          render: (row: TransactionEntity) => <TransactionTypeTag type={row.type} />,
        },
        {
          key: 'status',
          headerKey: 'common.status',
          render: (row: TransactionEntity) => <TransactionStatusTag status={row.status} />,
        },
        {
          key: 'restaurantBranchId',
          headerKey: 'common.branch',
          display: {
            kind: 'reference',
            load: branchReference,
            optionLabel: (branch: any) => branch.name,
          },
        },
      ],
      form: TransactionTypedForm,
      emptyState: <EmptyState />,
      toPayload: (values: any, params) => ({
        ...values,
        restaurantId: params.restaurantId,
        tags: values.tags?.split(',').map((tag: string) => tag.trim()) || [],
        total: Number(values.total),
      }),
      useTitleVars: () => ({
        restaurantName: currentRestaurant?.name,
        branchName: currentRestaurantBranch?.name,
        title: transactionTitle,
        titlePlural: transactionTitlePlural,
      }),
    }),
    toolbar: (state: { refetch: () => void }) => {
      refetchRef.current = state.refetch;
      return (
        <>
          {/* The admin's fixed range, stated rather than editable — see the hook doc. */}
          {isAdmin && (
            <Tag className="transactions__today" type="blue" size="md">
              {t('dateRanges.today')}
            </Tag>
          )}
          {/* Ghost, like the toolbar's other icon buttons (export, columns). */}
          <Button
            renderIcon={Renew}
            onClick={state.refetch}
            hasIconOnly
            kind="ghost"
            size="lg"
            tooltipPosition="bottom"
            iconDescription={t('common.refresh')}
          />
        </>
      );
    },
  };

  return { page };
};
